import { db, functions } from "../../main";
import { getClientRef, getClientRefById } from "./ClientRepository";
import { ClientPanelAssignment } from "../models/ClientPanelAssignment";
import { ProjectDetails, ProjectOperationSource, ProjectSyncTarget } from "@/data/models/ProjectDetails";
import clone from "clone";
import { firestore } from "firebase/app";
import { toEndOfDay } from "@/utils/DateHelper";
import { ProgramChain } from "../models/ProgramChain";
import { updateProjectStepsOnContactPersonDeletion } from "./ProjectStatusRepository";

function getMemberRef(memberClientId: string) {
  return getClientRefById(memberClientId) + "/clientPanelsAssignments";
}

function getRef() {
  return getClientRef() + "/clientPanelsAssignments";
}

export async function deleteClientPanelAssignment(
  clientPanelAssignment: ClientPanelAssignment
) {
  const clientPanelRef = `${getRef()}/${clientPanelAssignment.id}`;

  db.doc(clientPanelRef).delete();
}

export async function saveClientPanelAssignment(
  clientPanelAssignment: ClientPanelAssignment
) {
  const clientPanelRef = `${getRef()}/${clientPanelAssignment.id}`;

  db.doc(clientPanelRef).set(clientPanelAssignment, { merge: true });
}

export async function getClientPanelAssignment(assignmentId: string) {
  const result = await db
    .collection(getRef())
    .doc(assignmentId)
    .get();

  if (result) {
    return result.data() as ClientPanelAssignment;
  }
}

export async function getFirstAssignmentStartedBefore(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const assignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentStartDate", "<", timestamp.toDate())
      .orderBy("assignmentStartDate", "desc")
      .limit(1)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  return assignments[0];
}


export async function getFirstAssignmentEndedBefore(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const assignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentEndDate", "<", timestamp.toDate())
      .orderBy("assignmentEndDate", "desc")
      .limit(1)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  return assignments[0];
}

export async function getMemberClientPanelAssignmentsOfProjectAndSerialNumber(
  projectId: string,
  serialNumber: string,
  memberClientId: string
) {
  return (
    await db
      .collection(getMemberRef(memberClientId))
      .where("assignedProjectId", "==", projectId)
      .where("serialNumber", "==", serialNumber)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);
}

export async function getClientPanelAssignmentsOfProjectAndSerialNumber(
  projectId: string,
  serialNumber: string
) {
  return (
    await db
      .collection(getRef())
      .where("assignedProjectId", "==", projectId)
      .where("serialNumber", "==", serialNumber)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);
}

export async function getClientPanelAssignmentsOfProject(projectId: string) {
  return (
    await db
      .collection(getRef())
      .where("assignedProjectId", "==", projectId)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);
}

export async function updateProjectNameOfClientPanelAssignments(
  projectId: string,
  newProjectName: string,
  newProjectNumber: string
) {
  const result = await getClientPanelAssignmentsOfProject(projectId);

  return Promise.all([
    result.map((clientPanelAssignment) => {
      clientPanelAssignment.assignedProjectName = newProjectName;
      clientPanelAssignment.assignedProjectNumber = newProjectNumber;

      return saveClientPanelAssignment(clientPanelAssignment);
    }),
  ]);
}

export async function updateContactPersonOfClientPanelAssignments(
  toDeleteContactPersonNumber: number,
  newContactPersonId: string,
  assignmentsOfContactPerson: ClientPanelAssignment[],
  projectId: string,
  shouldUpdateProjectSteps?: boolean,
  syncTarget?: ProjectSyncTarget,
) {
  const promises = [
    ...assignmentsOfContactPerson.map((assignment) => {
      assignment.contactPersonId = newContactPersonId;
      return saveClientPanelAssignment(assignment);
    }),
    ...(shouldUpdateProjectSteps
      ? [
          updateProjectStepsOnContactPersonDeletion(
            projectId,
            toDeleteContactPersonNumber,
            syncTarget
          )
        ]
      : [])
  ];

  return Promise.all(promises);
}

export async function getNextAssignmentAfter(
  serialNumber: string,
  assignmentId: string,
  date: firestore.Timestamp
) {
  const result = await db
    .collection(getRef())
    .where("serialNumber", "==", serialNumber)
    .where("assignmentStartDate", ">=", date)
    .orderBy("assignmentStartDate", "asc")
    .limit(2)
    .get();

  return result.docs
    .map((doc) => doc.data() as ClientPanelAssignment)
    .filter((otherAssignment) => otherAssignment.id !== assignmentId);
}

export async function getNextAssignmentStartedAfter(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const assignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentStartDate", ">", timestamp.toDate())
      .orderBy("assignmentStartDate", "asc")
      .limit(1)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  return assignments[0];
}

export async function getAllAssignmentsStartedAfter(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const assignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentStartDate", ">", timestamp.toDate())
      .orderBy("assignmentStartDate", "asc")
      .orderBy("assignmentEndDate", "desc")
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  return assignments;
}

/**
 * Retrieves all assignments for a given serial number that either started or ended after the specified timestamp.
 *
 * @param {string} serialNumber - The serial number to filter assignments by.
 * @param {firestore.Timestamp} timestamp - The timestamp to filter assignments. Only assignments started or ended after this timestamp will be returned.
 * @returns {Promise<ClientPanelAssignment[]>} A promise that resolves to an array of ClientPanelAssignment objects.
 */
export async function getAllAssignmentsStartedOrEndedAfter(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  // Query for assignments that started after the given date
  const startAfterAssignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentStartDate", ">", timestamp.toDate())
      .orderBy("assignmentStartDate", "asc")
      .orderBy("assignmentEndDate", "desc")
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  // Query for assignments that ended after the given date
  const endAfterAssignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentEndDate", ">", timestamp.toDate())
      .orderBy("assignmentEndDate", "desc")
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  // Combine the results, removing duplicates
  const allAssignmentsMap = new Map<string, ClientPanelAssignment>();
  [...startAfterAssignments, ...endAfterAssignments].forEach((assignment) => {
    allAssignmentsMap.set(assignment.id, assignment);
  });

  const allAssignments = Array.from(allAssignmentsMap.values());

  // Sort the combined assignments
  allAssignments.sort((a: ClientPanelAssignment, b: ClientPanelAssignment) => {
    const startDateComparison = a.assignmentStartDate.toMillis() - b.assignmentStartDate.toMillis();
    if (startDateComparison !== 0) {
      return startDateComparison;
    }
    if (a.assignmentEndDate === null) return 1;
    if (b.assignmentEndDate === null) return -1;
    return b.assignmentEndDate.toMillis() - a.assignmentEndDate.toMillis();
  });

  return allAssignments;
}

export async function getFirstAssignmentEndedAfter(
  serialNumber: string,
  timestamp: firestore.Timestamp
) {
  const assignments = (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .where("assignmentEndDate", ">", timestamp.toDate())
      .orderBy("assignmentEndDate", "asc")
      .limit(1)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);

  return assignments[0];
}

export async function loadAssignmentsEndedAfter(
  serialNumber: string,
  assignmentId: string,
  date: firestore.Timestamp
) {
  const result = await db
    .collection(getRef())
    .where("serialNumber", "==", serialNumber)
    .where("assignmentEndDate", ">", toEndOfDay(date))
    .orderBy("assignmentEndDate", "asc")
    .limit(2)
    .get();

  return result.docs
    .map((doc) => doc.data() as ClientPanelAssignment)
    .filter((otherAssignment) => {
      if (otherAssignment.id === assignmentId) {
        return false;
      }

      return true;
    });
}

export function observeClientPanelAssignmentsOfProject(
  projectId: string,
  onNext: (clientPanelsAssignments: Array<ClientPanelAssignment>) => void
) {
  return db
    .collection(getRef())
    .where("assignedProjectId", "==", projectId)
    .onSnapshot((snapshot) => {
      onNext(snapshot.docs.map((doc) => doc.data() as ClientPanelAssignment));
    });
}

export async function handleClientPanelDeletion(
  assignment: ClientPanelAssignment
) {
  const promises: Array<Promise<void>> = [];

  promises.push(deleteClientPanelAssignment(assignment));

  return Promise.all([promises]);
}

export async function deleteClientPanelAssignments(
  targetProject: ProjectDetails,
  assignments: Array<ClientPanelAssignment>
) {
  if (assignments.length === 0) {
    return;
  }
  await Promise.all([
    assignments.map((assignment) => handleClientPanelDeletion(assignment)),
  ]);
}

export async function saveOrUpdateClientPanelAssignment(
  assignment: ClientPanelAssignment,
  programChains: ProgramChain[],
  changeMadeBy: string,
  changeMadeByUserId: string
) {
  const saveIresDeviceAssignment = functions.httpsCallable(
    "saveIresDeviceAssignment"
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = {
    ...clone(assignment),
    assignmentStartDate: assignment.assignmentStartDate
      ?.toDate()
      ?.toISOString(),
    assignmentEndDate: assignment.assignmentEndDate?.toDate()?.toISOString(),
  };

  if (data.meterReadingInfo?.meterReadingStartCorrectedBy) {
    data.meterReadingInfo.meterReadingStartCorrectedBy.recordedAt = data.meterReadingInfo.meterReadingStartCorrectedBy.recordedAt
      .toDate()
      .toISOString();
  }

  if (data.meterReadingInfo?.meterReadingEndCorrectedBy) {
    data.meterReadingInfo.meterReadingEndCorrectedBy.recordedAt = data.meterReadingInfo.meterReadingEndCorrectedBy.recordedAt
      .toDate()
      .toISOString();
  }

  await saveIresDeviceAssignment({
    assignment: data,
    programChainIds: programChains.map((chain) => chain.id),
    changeMadeBy: changeMadeBy,
    source: ProjectOperationSource.PORTAL,
    changeMadeByUserId: changeMadeByUserId,
  });
}

export async function getMemberClientPanelAllAssignments(
  serialNumber: string,  
  memberClientId: string
) {
  return (
    await db
      .collection(getMemberRef(memberClientId))
      .where("serialNumber", "==", serialNumber)
      .orderBy("assignmentEndDate", "asc")
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);
}

export async function getClientPanelAllAssignments(
  serialNumber: string,  
) {
  return (
    await db
      .collection(getRef())
      .where("serialNumber", "==", serialNumber)
      .orderBy("assignmentEndDate", "asc")
      .get()
  ).docs.map((doc) => doc.data() as ClientPanelAssignment);
}
